import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  X,
  Info,
  FileText,
  LinkIcon,
  Image as ImageIcon,
  Settings as SettingsIcon,
  Bell,
  BellOff,
  Search,
  Download,
  Trash2,
  Smartphone,
  Palette,
  User,
} from "lucide-react";
import { TerminalTheme, TERMINAL_THEMES } from "../types";
import { appendAuthToken } from "../lib/firebaseAuth";
import {
  getCurrentSubscription,
  getPushStatus,
  registerServiceWorker,
  subscribeToPush,
  unsubscribeFromPush,
  type PushStatus,
} from "../lib/push";

// One unified bottom-sheet that opens when the operator taps the agent
// avatar in the chat header. iOS-Messages × FaceTime info-panel feel.
// Tabs: About / Files / Links / Photos / Settings.
//
// Props are the bare minimum the drawer needs from its parent: the
// theme, the agent label, the message list it should mine for files /
// links / photos, the fetcher for talking to /api/push/*, and the
// currently-selected theme id + setter for the in-drawer Settings tab.

type ScopedFetch = (path: string, init?: RequestInit) => Promise<Response>;

interface DrawerMessage {
  role: "user" | "agent";
  text: string;
  ts?: string;
  filename?: string;
}

interface AgentDrawerProps {
  open: boolean;
  onClose: () => void;
  theme: TerminalTheme;
  agentLabel: string;
  messages: DrawerMessage[];
  fetcher: ScopedFetch;
  selectedThemeId: string;
  onSelectTheme: (id: string) => void;
  /** Optional user profile (display name + pic) for the Profile tab. Stored
   *  in this PWA's localStorage so it persists across /chat launches on the
   *  same device install. Agent identity is never editable here. */
  userProfile?: ChatUserProfile;
  onUpdateProfile?: (p: ChatUserProfile) => void;
  /** Local-only "deleted/hidden" sessions for the PWA install (addresses
   *  clutter from multiple Home-Screen adds after updates). Not server-side. */
  hiddenSessions?: string[];
  onHideSession?: (key: string) => void;
  onResetLocalData?: () => void;
}

type Tab = "about" | "profile" | "files" | "links" | "photos" | "settings";

interface ChatUserProfile {
  displayName: string;
  avatarDataUrl: string | null;
}

const ATTACHMENT_RE = /\[attachment:\s*([^\s\]]+)\s+\(([^)]+)\)\]/g;
const URL_RE = /(https?:\/\/[^\s<>"')]+)/g;
const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|heic|avif|svg)(?:\?|$)/i;

interface ParsedFile {
  path: string;
  name: string;
  size: string;
  url: string | null;
  ts: string;
  fromAgent: boolean;
}

interface ParsedLink {
  url: string;
  ts: string;
  fromAgent: boolean;
}

function parseFiles(messages: DrawerMessage[]): ParsedFile[] {
  const out: ParsedFile[] = [];
  for (const m of messages) {
    if (!m.text) continue;
    ATTACHMENT_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = ATTACHMENT_RE.exec(m.text)) !== null) {
      const path = match[1];
      const name = path.split("/").pop() || path;
      const url = path.startsWith("state/uploads/chat/")
        ? appendAuthToken(`/api/uploads/chat/${encodeURIComponent(name)}`)
        : null;
      out.push({
        path,
        name,
        size: match[2] || "",
        url,
        ts: m.ts || "",
        fromAgent: m.role === "agent",
      });
    }
  }
  return out.reverse(); // newest first
}

function parseLinks(messages: DrawerMessage[]): ParsedLink[] {
  const seen = new Set<string>();
  const out: ParsedLink[] = [];
  for (const m of messages) {
    if (!m.text) continue;
    URL_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = URL_RE.exec(m.text)) !== null) {
      const url = match[1].replace(/[.,;:!?)\]]+$/, "");
      if (seen.has(url)) continue;
      seen.add(url);
      out.push({ url, ts: m.ts || "", fromAgent: m.role === "agent" });
    }
  }
  return out.reverse();
}

function formatTs(ts: string): string {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay
      ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      : d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export const AgentDrawer: React.FC<AgentDrawerProps> = ({
  open,
  onClose,
  theme,
  agentLabel,
  messages,
  fetcher,
  selectedThemeId,
  onSelectTheme,
  userProfile,
  onUpdateProfile,
  hiddenSessions,
  onHideSession,
  onResetLocalData,
}) => {
  const [tab, setTab] = useState<Tab>("about");
  const [pushStatus, setPushStatus] = useState<PushStatus>(() =>
    getPushStatus(false)
  );
  const [pushBusy, setPushBusy] = useState(false);
  const [devices, setDevices] = useState<
    Array<{ id: string; label: string; user_agent: string; added_ts: number }>
  >([]);

  // Lock body scroll while drawer is open (iOS Safari quirk)
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Refresh push state + device list whenever drawer opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      await registerServiceWorker();
      const sub = await getCurrentSubscription();
      if (cancelled) return;
      setPushStatus(getPushStatus(!!sub));
      try {
        const res = await fetcher("/api/push/status");
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setDevices(data.subscriptions || []);
        }
      } catch {
        /* device list is best-effort */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, fetcher]);

  const files = useMemo(() => parseFiles(messages), [messages]);
  const links = useMemo(() => parseLinks(messages), [messages]);
  const photos = useMemo(
    () => files.filter((f) => IMAGE_EXT_RE.test(f.name)),
    [files]
  );

  const togglePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (pushStatus.subscribed) {
        await unsubscribeFromPush(fetcher);
      } else {
        await subscribeToPush(fetcher, `${agentLabel} chat`);
      }
      const sub = await getCurrentSubscription();
      setPushStatus(getPushStatus(!!sub));
      // Refresh device list after toggle
      try {
        const res = await fetcher("/api/push/status");
        if (res.ok) {
          const data = await res.json();
          setDevices(data.subscriptions || []);
        }
      } catch {
        /* ignore */
      }
    } finally {
      setPushBusy(false);
    }
  };

  const exportThread = () => {
    const lines = messages.map((m) => {
      const who = m.role === "agent" ? agentLabel : "Operator";
      const ts = m.ts ? `[${m.ts}] ` : "";
      return `${ts}${who}:\n${m.text}\n`;
    });
    const blob = new Blob([lines.join("\n---\n\n")], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `${agentLabel.toLowerCase()}-thread-${stamp}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
    >
      {/* Tap-outside-to-close scrim, then the sheet panel */}
      <div className="flex-1" />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: theme.bg,
          color: theme.fg,
          borderTopColor: theme.border,
          maxHeight: "90vh",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
        className="border-t rounded-t-3xl flex flex-col overflow-hidden"
      >
        {/* Pill grabber + header row */}
        <div className="pt-2 pb-1 flex justify-center">
          <div
            style={{ backgroundColor: theme.border }}
            className="h-1 w-12 rounded-full"
          />
        </div>
        <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div
              style={{ backgroundColor: theme.accent, color: theme.bg }}
              className="h-10 w-10 rounded-full flex items-center justify-center font-bold text-base flex-shrink-0"
            >
              {agentLabel.slice(0, 1).toUpperCase()}
            </div>
            <div>
              <div style={{ color: theme.fg }} className="font-semibold text-base">
                {agentLabel}
              </div>
              <div style={{ color: theme.systemText }} className="text-[12px]">
                {messages.length} messages · {files.length} files
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ color: theme.systemText }}
            className="p-2 rounded-full hover:opacity-70"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs — Profile is for the human (you) in this standard /chat PWA layer.
            Agent name is always shown and immutable per the Craig/Margo separation. */}
        <div
          style={{ borderBottomColor: theme.border }}
          className="border-b flex items-center gap-1 px-2 flex-shrink-0 overflow-x-auto"
        >
          {([
            { id: "about", label: "About", icon: Info },
            { id: "profile", label: "Profile", icon: User },
            { id: "files", label: "Files", icon: FileText },
            { id: "links", label: "Links", icon: LinkIcon },
            { id: "photos", label: "Photos", icon: ImageIcon },
            { id: "settings", label: "Settings", icon: SettingsIcon },
          ] as const).map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                style={{
                  color: active ? theme.accent : theme.systemText,
                  borderBottomColor: active ? theme.accent : "transparent",
                }}
                className="flex items-center gap-1.5 px-3 py-3 text-[13px] font-medium border-b-2 transition-colors flex-shrink-0"
              >
                <Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0">
          {tab === "about" && (
            <AboutTab
              theme={theme}
              agentLabel={agentLabel}
              messages={messages}
            />
          )}
          {tab === "profile" && (
            <ProfileTab
              theme={theme}
              agentLabel={agentLabel}
              userProfile={userProfile}
              onUpdateProfile={onUpdateProfile}
            />
          )}
          {tab === "files" && (
            <FilesTab theme={theme} files={files.filter((f) => !IMAGE_EXT_RE.test(f.name))} />
          )}
          {tab === "links" && <LinksTab theme={theme} links={links} />}
          {tab === "photos" && <PhotosTab theme={theme} photos={photos} />}
          {tab === "settings" && (
            <SettingsTab
              theme={theme}
              pushStatus={pushStatus}
              pushBusy={pushBusy}
              togglePush={togglePush}
              selectedThemeId={selectedThemeId}
              onSelectTheme={onSelectTheme}
              devices={devices}
              exportThread={exportThread}
              hiddenSessions={hiddenSessions}
              onHideSession={onHideSession}
              onResetLocalData={onResetLocalData}
              sessionsForManage={messages /* reused as signal; real list passed from parent */}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Tab implementations ────────────────────────────────────────────────

const AboutTab: React.FC<{
  theme: TerminalTheme;
  agentLabel: string;
  messages: DrawerMessage[];
}> = ({ theme, agentLabel, messages }) => {
  const lastAgentMsg = [...messages].reverse().find((m) => m.role === "agent");
  return (
    <div className="space-y-4">
      <Row label="Name" value={agentLabel} theme={theme} />
      <Row
        label="Messages in thread"
        value={String(messages.length)}
        theme={theme}
      />
      <Row
        label="Last agent reply"
        value={lastAgentMsg?.ts ? formatTs(lastAgentMsg.ts) : "—"}
        theme={theme}
      />
      <div
        style={{ backgroundColor: theme.inputBg, borderColor: theme.border }}
        className="border rounded-lg p-3 text-[13px] leading-relaxed"
      >
        <span style={{ color: theme.systemText }}>
          {agentLabel} is an always-on AI agent reachable over Tailscale. Push
          notifications, file delivery, and link sharing all happen in this
          conversation thread. Tap any other tab to browse what's been shared.
        </span>
      </div>
    </div>
  );
};

const FilesTab: React.FC<{ theme: TerminalTheme; files: ParsedFile[] }> = ({
  theme,
  files,
}) => {
  if (!files.length)
    return <EmptyState theme={theme} text="No files shared in this thread yet." />;
  return (
    <div className="flex flex-col gap-2">
      {files.map((f, i) => (
        <a
          key={`${f.path}-${i}`}
          href={f.url || undefined}
          target="_blank"
          rel="noreferrer"
          style={{
            backgroundColor: theme.inputBg,
            borderColor: theme.border,
            color: theme.fg,
            opacity: f.url ? 1 : 0.6,
          }}
          className="border rounded-lg p-3 flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <div
            style={{ backgroundColor: `${theme.accent}22`, color: theme.accent }}
            className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
          >
            <FileText size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-[14px] truncate">{f.name}</div>
            <div
              style={{ color: theme.systemText }}
              className="text-[12px] flex items-center gap-2"
            >
              <span>{f.size}</span>
              {f.ts && <span>· {formatTs(f.ts)}</span>}
              <span>· {f.fromAgent ? "from agent" : "from you"}</span>
            </div>
          </div>
          {f.url && (
            <Download size={16} style={{ color: theme.systemText }} />
          )}
        </a>
      ))}
    </div>
  );
};

const LinksTab: React.FC<{ theme: TerminalTheme; links: ParsedLink[] }> = ({
  theme,
  links,
}) => {
  if (!links.length)
    return <EmptyState theme={theme} text="No links shared in this thread yet." />;
  return (
    <div className="flex flex-col gap-2">
      {links.map((l, i) => {
        let host = l.url;
        try {
          host = new URL(l.url).host;
        } catch {
          /* keep raw url */
        }
        return (
          <a
            key={`${l.url}-${i}`}
            href={l.url}
            target="_blank"
            rel="noreferrer"
            style={{
              backgroundColor: theme.inputBg,
              borderColor: theme.border,
              color: theme.fg,
            }}
            className="border rounded-lg p-3 flex items-center gap-3 hover:opacity-80"
          >
            <div
              style={{ backgroundColor: `${theme.accent}22`, color: theme.accent }}
              className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
            >
              <LinkIcon size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[14px] truncate">{host}</div>
              <div
                style={{ color: theme.systemText }}
                className="text-[12px] truncate"
              >
                {l.url}
              </div>
            </div>
            {l.ts && (
              <span
                style={{ color: theme.systemText }}
                className="text-[11px] flex-shrink-0"
              >
                {formatTs(l.ts)}
              </span>
            )}
          </a>
        );
      })}
    </div>
  );
};

const PhotosTab: React.FC<{ theme: TerminalTheme; photos: ParsedFile[] }> = ({
  theme,
  photos,
}) => {
  if (!photos.length)
    return <EmptyState theme={theme} text="No photos shared in this thread yet." />;
  return (
    <div className="grid grid-cols-2 gap-2">
      {photos.map((p, i) => (
        <a
          key={`${p.path}-${i}`}
          href={p.url || undefined}
          target="_blank"
          rel="noreferrer"
          style={{ backgroundColor: theme.inputBg, borderColor: theme.border }}
          className="border rounded-lg aspect-square overflow-hidden block"
        >
          {p.url ? (
            <img
              src={p.url}
              alt={p.name}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div
              style={{ color: theme.systemText }}
              className="h-full w-full flex items-center justify-center text-[12px] p-2 text-center"
            >
              {p.name}
            </div>
          )}
        </a>
      ))}
    </div>
  );
};

const SettingsTab: React.FC<{
  theme: TerminalTheme;
  pushStatus: PushStatus;
  pushBusy: boolean;
  togglePush: () => void;
  selectedThemeId: string;
  onSelectTheme: (id: string) => void;
  devices: Array<{ id: string; label: string; user_agent: string; added_ts: number }>;
  exportThread: () => void;
  hiddenSessions?: string[];
  onHideSession?: (key: string) => void;
  onResetLocalData?: () => void;
  sessionsForManage?: any; // best-effort; parent can pass real list in future
}> = ({
  theme,
  pushStatus,
  pushBusy,
  togglePush,
  selectedThemeId,
  onSelectTheme,
  devices,
  exportThread,
  hiddenSessions = [],
  onHideSession,
  onResetLocalData,
}) => {
  const pushDisabled =
    pushBusy ||
    pushStatus.permission === "denied" ||
    pushStatus.permission === "needs-pwa-install" ||
    pushStatus.permission === "unsupported";

  return (
    <div className="space-y-5">
      {/* Notifications */}
      <Section title="Notifications" theme={theme}>
        <button
          type="button"
          onClick={pushDisabled ? undefined : togglePush}
          disabled={pushDisabled}
          style={{
            backgroundColor: theme.inputBg,
            borderColor: theme.border,
            color: theme.fg,
          }}
          className="w-full border rounded-lg p-3 flex items-center gap-3 disabled:opacity-60"
        >
          <div
            style={{
              backgroundColor: pushStatus.subscribed ? `${theme.accent}22` : `${theme.systemText}22`,
              color: pushStatus.subscribed ? theme.accent : theme.systemText,
            }}
            className="h-9 w-9 rounded-lg flex items-center justify-center"
          >
            {pushStatus.subscribed ? <Bell size={16} /> : <BellOff size={16} />}
          </div>
          <div className="flex-1 min-w-0 text-left">
            <div className="font-medium text-[14px]">
              {pushStatus.subscribed ? "Notifications on" : "Notifications off"}
            </div>
            <div style={{ color: theme.systemText }} className="text-[12px]">
              {pushStatus.permission === "needs-pwa-install"
                ? "iOS: open from your Home Screen first"
                : pushStatus.permission === "denied"
                  ? "Blocked in browser settings — flip there"
                  : pushStatus.permission === "unsupported"
                    ? "Not available in this browser"
                    : pushStatus.subscribed
                      ? "You'll be pinged when the agent replies"
                      : "Tap to enable lock-screen pings"}
            </div>
          </div>
        </button>
        {(pushStatus.permission === "needs-pwa-install" ||
          pushStatus.permission === "denied" ||
          pushStatus.permission === "unsupported") && (
          <div
            style={{ color: theme.systemText, borderColor: theme.border }}
            className="mt-2 text-[12px] border rounded-lg p-2.5 leading-relaxed"
          >
            {pushStatus.permission === "needs-pwa-install" ? (
              <>
                📲 On iPhone/iPad, notifications only work once this app is on
                your Home Screen. Tap <b>Share</b> → <b>Add to Home Screen</b>,
                open it from that new icon, then flip this on.
              </>
            ) : pushStatus.permission === "denied" ? (
              <>
                🔕 Notifications are blocked for this site. Re-enable them in your
                iOS/browser <b>Settings → Notifications</b>, then come back and
                flip this on.
              </>
            ) : (
              <>
                🚫 This browser can't receive web-push notifications — it needs a
                secure <b>https://</b> connection and a modern browser. Open the
                dashboard at its https address in Safari/Chrome to enable them.
              </>
            )}
          </div>
        )}
      </Section>

      {/* Theme */}
      <Section title="Theme" theme={theme}>
        <div
          style={{ backgroundColor: theme.inputBg, borderColor: theme.border }}
          className="border rounded-lg p-3 flex items-center gap-3"
        >
          <div
            style={{ backgroundColor: `${theme.accent}22`, color: theme.accent }}
            className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
          >
            <Palette size={16} />
          </div>
          <select
            value={selectedThemeId}
            onChange={(e) => onSelectTheme(e.target.value)}
            style={{ color: theme.fg, backgroundColor: "transparent" }}
            className="flex-1 bg-transparent border-none text-[14px] font-medium focus:outline-none cursor-pointer"
            aria-label="Theme"
          >
            {TERMINAL_THEMES.map((t) => (
              <option
                key={t.id}
                value={t.id}
                style={{ backgroundColor: t.bg, color: t.fg }}
              >
                {t.name}
              </option>
            ))}
          </select>
        </div>
      </Section>

      {/* Devices */}
      <Section title={`Devices (${devices.length})`} theme={theme}>
        {devices.length === 0 ? (
          <EmptyState theme={theme} text="No devices subscribed to push yet." />
        ) : (
          <div className="flex flex-col gap-2">
            {devices.map((d) => (
              <div
                key={d.id}
                style={{ backgroundColor: theme.inputBg, borderColor: theme.border }}
                className="border rounded-lg p-3 flex items-center gap-3"
              >
                <div
                  style={{ backgroundColor: `${theme.accent}22`, color: theme.accent }}
                  className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                >
                  <Smartphone size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[13px] truncate">
                    {d.label || "Unnamed device"}
                  </div>
                  <div
                    style={{ color: theme.systemText }}
                    className="text-[11px] truncate"
                  >
                    {d.user_agent || "—"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Export */}
      <Section title="Conversation" theme={theme}>
        <button
          type="button"
          onClick={exportThread}
          style={{
            backgroundColor: theme.inputBg,
            borderColor: theme.border,
            color: theme.fg,
          }}
          className="w-full border rounded-lg p-3 flex items-center gap-3"
        >
          <div
            style={{ backgroundColor: `${theme.accent}22`, color: theme.accent }}
            className="h-9 w-9 rounded-lg flex items-center justify-center"
          >
            <Download size={16} />
          </div>
          <div className="flex-1 min-w-0 text-left">
            <div className="font-medium text-[14px]">Export thread</div>
            <div style={{ color: theme.systemText }} className="text-[12px]">
              Save the whole conversation as a .txt file
            </div>
          </div>
        </button>
      </Section>

      {/* PWA Sessions & local data (addresses "new session every reinstall/update") */}
      <Section title="This PWA install" theme={theme}>
        <div
          style={{ backgroundColor: theme.inputBg, borderColor: theme.border }}
          className="border rounded-lg p-3 text-[12px] leading-snug mb-2"
        >
          <span style={{ color: theme.systemText }}>
            Each time you add/re-add this chat to your Home Screen after an update, iOS
            can give it fresh local storage. Your real history lives on the server.
            Use the tools below to hide old clutter or start clean without a full reinstall.
          </span>
        </div>
        <button
          type="button"
          onClick={() => onResetLocalData && onResetLocalData()}
          disabled={!onResetLocalData}
          style={{
            backgroundColor: theme.inputBg,
            borderColor: theme.border,
            color: theme.fg,
          }}
          className="w-full border rounded-lg p-3 flex items-center gap-3 disabled:opacity-50"
        >
          <div
            style={{ backgroundColor: `${theme.accent}22`, color: theme.accent }}
            className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
          >
            <Trash2 size={16} />
          </div>
          <div className="flex-1 min-w-0 text-left">
            <div className="font-medium text-[14px]">Reset local data</div>
            <div style={{ color: theme.systemText }} className="text-[12px]">
              Clear profile, hidden sessions, selected thread, and prefs for this install
            </div>
          </div>
        </button>
        {hiddenSessions.length > 0 && (
          <button
            type="button"
            onClick={() => { /* parent can expose clear-hidden if wanted; for now reset covers */ }}
            style={{ color: theme.systemText }}
            className="mt-2 text-[11px] underline"
          >
            {hiddenSessions.length} session(s) hidden locally
          </button>
        )}
      </Section>
    </div>
  );
};

// ─── Tiny helpers ───────────────────────────────────────────────────────

const Row: React.FC<{ label: string; value: string; theme: TerminalTheme }> = ({
  label,
  value,
  theme,
}) => (
  <div className="flex items-center justify-between text-[14px]">
    <span style={{ color: theme.systemText }}>{label}</span>
    <span style={{ color: theme.fg }} className="font-medium">
      {value}
    </span>
  </div>
);

const Section: React.FC<{
  title: string;
  theme: TerminalTheme;
  children: React.ReactNode;
}> = ({ title, theme, children }) => (
  <div>
    <div
      style={{ color: theme.systemText }}
      className="text-[11px] uppercase tracking-wider font-semibold px-1 pb-1.5"
    >
      {title}
    </div>
    {children}
  </div>
);

const EmptyState: React.FC<{ theme: TerminalTheme; text: string }> = ({
  theme,
  text,
}) => (
  <div
    style={{ color: theme.systemText }}
    className="text-center py-12 text-[13px]"
  >
    {text}
  </div>
);

// ─── Profile tab (human side, standard /chat PWA layer) ──────────────────
// Lets the user set a display name (sent as principal on messages) and
// upload a profile picture (stored as data URL in this PWA's localStorage).
// The agent's name is ALWAYS shown and CANNOT be changed from here.

const ProfileTab: React.FC<{
  theme: TerminalTheme;
  agentLabel: string;
  userProfile?: ChatUserProfile;
  onUpdateProfile?: (p: ChatUserProfile) => void;
}> = ({ theme, agentLabel, userProfile, onUpdateProfile }) => {
  const [name, setName] = useState(userProfile?.displayName || "Operator");
  const [avatar, setAvatar] = useState<string | null>(userProfile?.avatarDataUrl || null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Keep local form in sync when parent profile changes (e.g. reset)
  useEffect(() => {
    setName(userProfile?.displayName || "Operator");
    setAvatar(userProfile?.avatarDataUrl || null);
  }, [userProfile]);

  const save = () => {
    if (!onUpdateProfile) return;
    setSaving(true);
    const next: ChatUserProfile = {
      displayName: (name || "Operator").trim().slice(0, 60),
      avatarDataUrl: avatar,
    };
    onUpdateProfile(next);
    // small delay so user sees the save affordance on slow devices
    window.setTimeout(() => setSaving(false), 180);
  };

  const onFile = (f: File | null) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      alert("Please choose an image file (PNG, JPG, HEIC, etc.)");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      alert("Image too large (max ~2 MB). Please choose a smaller photo.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setAvatar(dataUrl);
      // Auto-save on successful read so the pic appears immediately in chat bubbles
      if (onUpdateProfile) {
        onUpdateProfile({
          displayName: (name || "Operator").trim().slice(0, 60),
          avatarDataUrl: dataUrl,
        });
      }
    };
    reader.readAsDataURL(f);
  };

  const removePhoto = () => {
    setAvatar(null);
    if (onUpdateProfile) {
      onUpdateProfile({
        displayName: (name || "Operator").trim().slice(0, 60),
        avatarDataUrl: null,
      });
    }
  };

  const initials = (name || "O").slice(0, 1).toUpperCase();

  return (
    <div className="space-y-5">
      <div
        style={{ backgroundColor: theme.inputBg, borderColor: theme.border }}
        className="border rounded-lg p-3 text-[12px] leading-snug"
      >
        <span style={{ color: theme.systemText }}>
          This is <span style={{ color: theme.fg, fontWeight: 600 }}>your</span> profile in this
          chat companion. It is private to this PWA install on this device. The agent (
          {agentLabel}) name is always shown above and cannot be changed here.
        </span>
      </div>

      {/* Your avatar + name */}
      <div className="flex items-center gap-4">
        <div
          style={{ backgroundColor: theme.accent, color: theme.bg }}
          className="h-16 w-16 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 ring-1 ring-white/10"
        >
          {avatar ? (
            <img src={avatar} alt="Your profile" className="h-full w-full object-cover" />
          ) : (
            <span className="text-2xl font-bold">{initials}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div style={{ color: theme.systemText }} className="text-[11px] uppercase tracking-wider">
            Your display name
          </div>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => { if (e.key === "Enter") { (e.target as HTMLInputElement).blur(); save(); } }}
            placeholder="Operator"
            style={{ backgroundColor: theme.bg, color: theme.fg, borderColor: theme.border }}
            className="mt-1 w-full border rounded-lg px-3 py-2 text-[15px] font-medium focus:outline-none"
            maxLength={60}
          />
          <div style={{ color: theme.systemText }} className="mt-1 text-[11px]">
            Used as your sender name when you message {agentLabel}.
          </div>
        </div>
      </div>

      {/* Upload controls */}
      <div>
        <div style={{ color: theme.systemText }} className="text-[11px] uppercase tracking-wider mb-1.5 px-0.5">
          Profile picture
        </div>
        <div
          className="profile-upload-zone rounded-xl p-4 flex flex-col items-center justify-center text-center"
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); if (fileRef.current) fileRef.current.value = ""; }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            style={{ backgroundColor: theme.accent, color: theme.bg }}
            className="px-4 py-2 rounded-full text-[13px] font-semibold active:opacity-90"
          >
            Upload photo
          </button>
          <div style={{ color: theme.systemText }} className="mt-2 text-[11px]">
            JPG, PNG, HEIC up to 2 MB. Appears next to your messages.
          </div>
          {avatar && (
            <button
              type="button"
              onClick={removePhoto}
              style={{ color: "#f43f5e" }}
              className="mt-3 text-[12px] underline"
            >
              Remove photo
            </button>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={save}
        disabled={saving || !onUpdateProfile}
        style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.fg }}
        className="w-full border rounded-lg py-2.5 text-[13px] font-medium disabled:opacity-60"
      >
        {saving ? "Saved ✓" : "Save name"}
      </button>
    </div>
  );
};
